import React, { useState, useEffect, useReducer } from "react";
import openSocket from "../../services/socket-io";

import {
  Hidden,
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  InputAdornment,
  TextField,
  Chip,
} from "@material-ui/core";
import { Edit, DeleteOutline, AddBox, Search, Refresh } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TenantsServerModal from "../../components/T_tenantsServerModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

const reducer = (state, action) => {
  if (action.type === "LOAD_TENTANTS") {
    const tenantsServers = action.payload;
    const newTenantsServers = [];

    tenantsServers.forEach((tenantsServer) => {
      const tenantsServerIndex = state.findIndex((q) => q.id === tenantsServer.id);
      if (tenantsServerIndex !== -1) {
        state[tenantsServerIndex] = tenantsServer;
      } else {
        newTenantsServers.push(tenantsServer);
      }
    });

    return [...state, ...newTenantsServers];
  }

  if (action.type === "UPDATE_TENTANTS") {
    const tenantsServer = action.payload;
    const tenantsServerIndex = state.findIndex((q) => q.id === tenantsServer.id);

    if (tenantsServerIndex !== -1) {
      state[tenantsServerIndex] = tenantsServer;
      return [...state];
    } else {
      return [tenantsServer, ...state];
    }
  }

  if (action.type === "DELETE_TENTANTS") {
    const tenantsServerId = action.payload;

    const tenantsServerIndex = state.findIndex((q) => q.id === tenantsServerId);
    if (tenantsServerIndex !== -1) {
      state.splice(tenantsServerIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const TenantsServers = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [tenantsServers, dispatch] = useReducer(reducer, []);
  const [selectedTenantsServers, setSelectedTenantsServers] = useState(null);
  const [tenantsServersModalOpen, setTenantsServerModalOpen] = useState(false);
  const [deletingTenantsServers, setDeletingTenantsServers] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [apiname, setApiname] = useState("");

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTenantsServers = async () => {
        try {
          const { data } = await api.get("/tenantsServers/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_TENTANTS", payload: data.tenantsServers });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchTenantsServers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("tenantsServer", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TENTANTS", payload: data.tenantsServer });
      }

      if (data.action === "createDomain") {
        data.status ? toast.success(i18n.t("Domínio criado com sucesso!")) : toast.error(i18n.t("Erro ao criar domínio!"));
      }

      if (data.action === "createDomainCertificate") {
        data.status ? toast.success(i18n.t("Certificado de domínio criado com sucesso!")) : toast.error(i18n.t("Erro ao criar certificado de domínio!"));
      }

      if (data.action === "createDatabase") {
        data.status ? toast.success(i18n.t("Banco de dados criado com sucesso!")) : toast.error(i18n.t("Erro ao criar banco de dados!"));
      }

      if (data.action === "initializedServer") {
        data.status ? toast.success(i18n.t("Servidor inicializado com sucesso!")) : toast.error(i18n.t("Erro ao inicializar servidor!"));
      }

      if (data.action === "delete") {
        dispatch({
          type: "DELETE_TENTANTS",
          payload: +data.tenantsServerId,
        });
      }      
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenTenantsServerModal = () => {
    const name = window.prompt(i18n.t("global.buttons.add") + " - Digite um nome para o novo servidor:");
    if (name && name.trim()) {
      setApiname(name.trim());
      setSelectedTenantsServers(null);
      setTenantsServerModalOpen(true);
    }
  };

  const handleCloseTenantsServerModal = () => {
    setSelectedTenantsServers(null);
    setTenantsServerModalOpen(false);
    setApiname("");
  };

  const handleEditTenantsServers = (tenantsServer) => {
    setSelectedTenantsServers(tenantsServer);
    setTenantsServerModalOpen(true);
  };
    const handleRestartTenantsServers = async (tenantsServerId) => {
    try {
      await api.post(`/tenantsServers/${tenantsServerId}/restart`);
      toast.success(i18n.t("global.restarted"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteTenantsServers = async (tenantsServerId) => {
    try {
      await api.delete(`/tenantsServers/${tenantsServerId}`);
      toast.success(i18n.t("global.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTenantsServers(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingTenantsServers &&
          `${i18n.t("global.deleteTitle")} ${deletingTenantsServers.dbName}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteTenantsServers(deletingTenantsServers.id)}
      >
        {i18n.t("global.deleteMessage")}
      </ConfirmationModal>
      <TenantsServerModal
        open={tenantsServersModalOpen}
        onClose={handleCloseTenantsServerModal}
        aria-labelledby="form-dialog-title"
        tenantsServerId={selectedTenantsServers && selectedTenantsServers.id}
        apiname={apiname}
      />
      <MainHeader>
        <Title>{i18n.t("tenantsServer.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Hidden mdDown>
            <TextField
              placeholder={i18n.t("global.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Hidden>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenTenantsServerModal}
          >
            <AddBox /> <Hidden mdDown>{i18n.t("global.buttons.add")}</Hidden>
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Hidden lgUp>
        <TextField
          placeholder={i18n.t("global.searchPlaceholder")}
          type="search"
          value={searchParam}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search style={{ color: "gray" }} />
              </InputAdornment>
            ),
          }}
        />
        <br />
      </Hidden>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">#ID</TableCell>
              <TableCell align="center">Tenant</TableCell>
              <TableCell align="center">DB Name</TableCell>
              <TableCell align="center">Servidor Local</TableCell>
              <TableCell align="center">Servidor DB</TableCell>
              <TableCell align="center">Backend URL</TableCell>
              <TableCell align="center">Porta</TableCell>
              <TableCell align="center">WA Mode</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">{i18n.t("global.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {tenantsServers.map((tenantsServer) => (
                <TableRow key={tenantsServer.id}>
                  <TableCell align="center">#{tenantsServer.id}</TableCell>
                  <TableCell align="center">
                    {tenantsServer.tenant?.nickname}
                  </TableCell>
                  <TableCell align="center">{tenantsServer.dbName}</TableCell>
                  <TableCell align="center">
                    {tenantsServer.localeServer?.name}
                  </TableCell>
                  <TableCell align="center">
                    {tenantsServer.dbServer?.name}
                  </TableCell>
                  <TableCell align="center">
                    <a href={tenantsServer.backendUrl} target="_blank" rel="noreferrer">
                      {tenantsServer.backendUrl}
                    </a>
                  </TableCell>
                  <TableCell align="center">{tenantsServer.port}</TableCell>
                  <TableCell align="center">
                    <Chip label={tenantsServer.waMode} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    {tenantsServer.useChrome ? "✅" : "❌"}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleRestartTenantsServers(tenantsServer.id)}
                    >
                      <Refresh />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEditTenantsServers(tenantsServer)}
                    >
                      <Edit />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingTenantsServers(tenantsServer);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={10} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default TenantsServers;
